import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth, API } from '../App';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { 
  CheckCircle2, Calendar, Clock, Mail, ArrowRight, 
  Loader2, Video, AlertCircle 
} from 'lucide-react';
import confetti from 'canvas-confetti';

export default function BookingSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [booking, setBooking] = useState(null);
  
  const bookingId = searchParams.get('booking_id');
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    verifyPayment();
  }, [bookingId, sessionId]);

  const verifyPayment = async () => {
    if (!bookingId || !sessionId) {
      setError('Invalid booking reference');
      setLoading(false);
      return;
    }

    try {
      const res = await axios.get(
        `${API}/bookings/verify-payment/${bookingId}?session_id=${sessionId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (res.data.success) {
        setBooking(res.data.booking);
        // Trigger celebration confetti
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 },
          colors: ['#22c55e', '#3b82f6', '#8b5cf6']
        });
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to verify payment');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { 
      weekday: 'long', 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-500 mx-auto mb-4" />
          <p className="text-slate-600">Verifying your payment...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center"
        >
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-500" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Payment Issue</h1>
          <p className="text-slate-600 mb-6">{error}</p>
          <Button onClick={() => navigate('/dashboard')} className="w-full">
            Return to Dashboard
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-blue-50 to-slate-50 flex items-center justify-center p-4">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-3xl shadow-2xl p-8 md:p-12 max-w-lg w-full"
      >
        {/* Success Icon */}
        <motion.div 
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", stiffness: 200, delay: 0.2 }}
          className="w-24 h-24 bg-gradient-to-br from-green-400 to-green-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg shadow-green-500/30"
        >
          <CheckCircle2 className="w-12 h-12 text-white" />
        </motion.div>

        {/* Title */}
        <h1 className="text-3xl font-bold text-center text-slate-900 mb-2">
          Booking Confirmed!
        </h1>
        <p className="text-center text-slate-600 mb-8">
          Thank you for booking a consultation with Ahmad Raza
        </p>

        {/* Booking Details Card */}
        {booking && (
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6 mb-6 border border-blue-100"
          >
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Date</p>
                  <p className="font-semibold text-slate-900">{formatDate(booking.date)}</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-indigo-500 rounded-lg flex items-center justify-center">
                  <Clock className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Time</p>
                  <p className="font-semibold text-slate-900">{booking.time_slot} (UK Time)</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center">
                  <Video className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Topic</p>
                  <p className="font-semibold text-slate-900">{booking.topic}</p>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Important Notice */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6"
        >
          <div className="flex gap-3">
            <Mail className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-amber-800 mb-1">
                Zoom Link Coming Soon
              </p>
              <p className="text-sm text-amber-700">
                You will receive your Zoom meeting link via email at{' '}
                <span className="font-medium">{booking?.user_email}</span>{' '}
                within <span className="font-bold">8 hours</span>.
              </p>
            </div>
          </div>
        </motion.div>

        {/* What's Next */}
        <div className="text-center mb-6">
          <p className="text-sm text-slate-500">
            Need to make changes? Contact us at{' '}
            <a href="mailto:support@innovatorfoundervisas.com" className="text-blue-600 hover:underline">
              support@innovatorfoundervisas.com
            </a>
          </p>
        </div>

        {/* Action Button */}
        <Button 
          onClick={() => navigate('/dashboard')} 
          className="w-full bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 py-4 text-lg"
        >
          Go to Dashboard
          <ArrowRight className="w-5 h-5 ml-2" />
        </Button>
      </motion.div>
    </div>
  );
}
